// components/Division/DivisionList.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import DivisionForm from './DivisionForm';
import { divisionService } from '../../services/divisionService';
import { PencilIcon, TrashIcon, EyeIcon, EllipsisVerticalIcon, CubeIcon } from '@heroicons/react/24/outline';
import DataTable from 'react-data-table-component';

/* UI-only port to the Odoo-style chrome, matching EmployeeList.jsx /
   BranchList.jsx. Every piece of behaviour is unchanged: same state, same
   service calls, same DataTable, same columns, same row actions, same
   search. What changed is presentation only — colours resolve through
   theme.css vars, so this screen follows light/dark with no extra work. */

const DivisionList = () => {
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editingDivision, setEditingDivision] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [actionDropdown, setActionDropdown] = useState(null);
  const dropdownRefs = useRef({});

  useEffect(() => {
    loadDivisions();
  }, []);

  const loadDivisions = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await divisionService.getDivisions();
      setDivisions(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load divisions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this division?')) return;
    try {
      await divisionService.deleteDivision(id);
      await loadDivisions();
      setActionDropdown(null);
    } catch (err) {
      console.error(err);
      setError('Failed to delete division');
    }
  };

  const handleEdit = (division) => {
    setEditingDivision(division);
    setShowForm(true);
    setActionDropdown(null);
  };

  const handleView = (division) => {
    const details = [
      `Division Name: ${division.name}`,
      `Department: ${division.department?.name || 'Not specified'}`,
      `Branch: ${division.department?.branch?.name || 'Not specified'}`,
      `Company: ${division.department?.branch?.company?.name || 'Not specified'}`,
      `Employees: ${division.employees?.length || 0}`
    ].join('\n');
    alert(details);
    setActionDropdown(null);
  };

  const handleActionClick = (rowId, e) => {
    e.stopPropagation();
    setActionDropdown(actionDropdown === rowId ? null : rowId);
  };

  // Filter divisions based on search
  const filteredDivisions = useMemo(() => {
    if (!search.trim()) return divisions;

    return divisions.filter(division =>
      division.name?.toLowerCase().includes(search.toLowerCase()) ||
      division.department?.name?.toLowerCase().includes(search.toLowerCase()) ||
      division.department?.branch?.name?.toLowerCase().includes(search.toLowerCase()) ||
      division.department?.branch?.company?.name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [divisions, search]);

  const badge = (tone) => ({
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    background: `var(--b-${tone}-bg)`,
    color: `var(--b-${tone}-fg)`
  });

  // Define columns for DataTable
  const columns = [
    {
      name: '',
      cell: () => (
        <div
          className="flex h-7 w-7 items-center justify-center rounded-full"
          style={{ background: 'var(--tint)', color: 'var(--tint-ink)' }}
        >
          <CubeIcon className="h-[15px] w-[15px]" />
        </div>
      ),
      width: '54px',
      center: true
    },
    {
      name: 'Division',
      selector: row => row.name,
      sortable: true,
      cell: row => (
        <div className="min-w-0">
          <div className="truncate text-[12.5px] font-semibold" style={{ color: 'var(--ink)' }}>
            {row.name}
          </div>
          <div className="truncate text-[11px]" style={{ color: 'var(--faint)' }}>
            {row.department?.name}{row.department?.name && row.department?.branch?.name ? ' • ' : ''}{row.department?.branch?.name}
          </div>
        </div>
      ),
      minWidth: '200px',
      grow: 1
    },
    {
      name: 'Department',
      selector: row => row.department?.name,
      sortable: true,
      cell: row => (
        <span className="text-[12.5px]" style={{ color: row.department?.name ? 'var(--body-2)' : 'var(--dim)' }}>
          {row.department?.name || '—'}
        </span>
      ),
      minWidth: '150px'
    },
    {
      name: 'Branch',
      selector: row => row.department?.branch?.name,
      sortable: true,
      cell: row => (
        <span className="text-[12.5px]" style={{ color: row.department?.branch?.name ? 'var(--body-2)' : 'var(--dim)' }}>
          {row.department?.branch?.name || '—'}
        </span>
      ),
      minWidth: '150px'
    },
    {
      name: 'Company',
      selector: row => row.department?.branch?.company?.name,
      sortable: true,
      cell: row => (
        <span className="text-[12.5px]" style={{ color: row.department?.branch?.company?.name ? 'var(--body-2)' : 'var(--dim)' }}>
          {row.department?.branch?.company?.name || '—'}
        </span>
      ),
      minWidth: '150px'
    },
    {
      name: 'Employees',
      selector: row => row.employees?.length,
      sortable: true,
      cell: row => <span style={badge('green')}>{row.employees?.length || 0}</span>,
      width: '120px'
    },
    {
      name: '',
      cell: row => (
        <div
          ref={el => dropdownRefs.current[row.id] = el}
          className="action-dropdown-container relative"
        >
          <button
            onClick={(e) => handleActionClick(row.id, e)}
            className="rounded-[3px] p-1.5 transition-colors"
            style={{ color: 'var(--dim)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--hover)'; e.currentTarget.style.color = 'var(--body)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dim)'; }}
          >
            <EllipsisVerticalIcon className="h-4 w-4" />
          </button>

          {actionDropdown === row.id && (
            <div
              className="absolute right-0 z-50 mt-1 w-44 -translate-y-1 transform overflow-hidden rounded-[4px] py-1"
              style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow)' }}
            >
              <button
                onClick={(e) => { e.stopPropagation(); handleView(row); }}
                className="flex w-full items-center px-3 py-[7px] text-left text-[12.5px] transition-colors"
                style={{ color: 'var(--body)' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <EyeIcon className="mr-2.5 h-4 w-4" style={{ color: 'var(--accent)' }} />
                View Details
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
                className="flex w-full items-center px-3 py-[7px] text-left text-[12.5px] transition-colors"
                style={{ color: 'var(--body)' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <PencilIcon className="mr-2.5 h-4 w-4" style={{ color: 'var(--accent)' }} />
                Edit Division
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
                className="flex w-full items-center px-3 py-[7px] text-left text-[12.5px] transition-colors"
                style={{ color: 'var(--neg)', borderTop: '1px solid var(--line-soft)', marginTop: 4, paddingTop: 9 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <TrashIcon className="mr-2.5 h-4 w-4" />
                Delete Division
              </button>
            </div>
          )}
        </div>
      ),
      width: '56px',
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ];

  // Custom styles for DataTable — flat, hairline, token-driven.
  const customStyles = {
    table: { style: { backgroundColor: 'var(--surface)' } },
    head: {
      style: {
        fontSize: '11.5px',
        fontWeight: '600',
        color: 'var(--muted)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    headRow: {
      style: {
        minHeight: '32px',
        borderBottomWidth: '1px',
        borderBottomColor: 'var(--line)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    headCells: {
      style: {
        paddingLeft: '10px',
        paddingRight: '10px',
        fontWeight: '600',
        letterSpacing: '0',
        textTransform: 'none',
        fontSize: '11.5px',
        color: 'var(--muted)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    cells: {
      style: {
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingTop: '8px',
        paddingBottom: '8px',
        fontSize: '12.5px',
        color: 'var(--body-2)'
      }
    },
    rows: {
      style: {
        minHeight: '40px',
        backgroundColor: 'var(--surface)',
        color: 'var(--body-2)',
        '&:not(:last-of-type)': {
          borderBottomWidth: '1px',
          borderBottomColor: 'var(--line-soft)'
        },
        '&:hover': { backgroundColor: 'var(--row-sel)' }
      },
      stripedStyle: { backgroundColor: 'var(--surface)' }
    },
    pagination: {
      style: {
        backgroundColor: 'var(--surface)',
        color: 'var(--muted)',
        fontSize: '12px',
        borderTopWidth: '1px',
        borderTopColor: 'var(--line)',
        minHeight: '42px'
      },
      pageButtonsStyle: {
        fill: 'var(--muted)',
        '&:disabled': { fill: 'var(--dim)' },
        '&:hover:not(:disabled)': { backgroundColor: 'var(--hover)' }
      }
    },
    noData: { style: { backgroundColor: 'var(--surface)' } },
    progress: { style: { backgroundColor: 'var(--surface)' } }
  };

  const LoadingComponent = () => (
    <div className="flex items-center justify-center py-12" style={{ background: 'var(--surface)' }}>
      <div
        className="h-6 w-6 animate-spin rounded-full border-b-2"
        style={{ borderColor: 'var(--accent)' }}
      />
      <span className="ml-3 text-[12.5px]" style={{ color: 'var(--muted)' }}>Loading divisions…</span>
    </div>
  );

  const NoDataComponent = () => (
    <div
      className="flex flex-col items-center justify-center px-6 py-14 text-center"
      style={{ background: 'var(--surface)' }}
    >
      <div
        className="mb-4 flex h-12 w-12 items-center justify-center rounded-full"
        style={{ background: 'var(--tint)' }}
      >
        <CubeIcon className="h-6 w-6" style={{ color: 'var(--tint-ink)' }} />
      </div>
      <h3 className="mb-1 text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>No divisions found</h3>
      <p className="mb-4 text-[12.5px]" style={{ color: 'var(--faint)' }}>
        {search ? 'Try adjusting your search terms' : 'Get started by creating your first division'}
      </p>
      {!search && (
        <button
          onClick={() => { setEditingDivision(null); setShowForm(true); }}
          className="h-7 rounded-[3px] px-3 text-[12.5px] font-semibold"
          style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}
        >
          New
        </button>
      )}
    </div>
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      let clickedInsideDropdown = false;

      Object.values(dropdownRefs.current).forEach(ref => {
        if (ref && ref.contains(event.target)) {
          clickedInsideDropdown = true;
        }
      });

      if (!clickedInsideDropdown) {
        setActionDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const totalEmployees = divisions.reduce((acc, division) => acc + (division.employees?.length || 0), 0);

  return (
    <div className="min-h-full" style={{ background: 'var(--bg)' }}>
      {/* ══ CONTROL PANEL ══ */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-[14px] pb-2 pt-[9px]">
          <h1
            className="m-0 truncate text-[18px] font-semibold tracking-[-.2px]"
            style={{ color: 'var(--ink)' }}
          >
            Divisions
          </h1>

          <div className="flex items-center gap-[6px]">
            <button
              onClick={() => { setEditingDivision(null); setShowForm(true); }}
              className="h-7 rounded-[3px] px-[13px] text-[12.5px] font-semibold"
              style={{ background: 'var(--accent)', color: 'var(--on-accent)', border: 0, cursor: 'pointer' }}
            >
              New
            </button>
          </div>

          <div className="min-w-[100px] flex-1" />

          {/* Search — Odoo control-panel style: one hairline box */}
          <div
            className="flex min-w-[200px] max-w-[420px] flex-1 items-center gap-[6px] rounded-[3px] py-[2px] pl-2 pr-1"
            style={{ background: 'var(--surface)', border: '1px solid var(--line-3)' }}
          >
            <svg
              className="h-[13px] w-[13px] flex-shrink-0"
              style={{ color: 'var(--faint)' }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search name, department, branch or company…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-6 min-w-[70px] flex-1 border-0 bg-transparent text-[12.5px] outline-none"
              style={{ color: 'var(--body)' }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="px-1 text-[14px] leading-none"
                style={{ color: 'var(--faint)', background: 'transparent', border: 0, cursor: 'pointer' }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          {/* Record counter */}
          <div className="flex items-center gap-[10px] text-[12px]" style={{ color: 'var(--muted)' }}>
            <span>
              {filteredDivisions.length}{search ? ` / ${divisions.length}` : ''}
              {' '}record{filteredDivisions.length === 1 ? '' : 's'}
            </span>
            <span style={{ width: 1, height: 15, background: 'var(--line-3)' }} />
            <span>{totalEmployees} employees</span>
          </div>
        </div>
      </div>

      <div className="p-[14px]">
        {/* Form */}
        {showForm && (
          <div className="mb-[14px]">
            <DivisionForm
              initialData={editingDivision}
              isEditing={!!editingDivision}
              onSubmit={async (data) => {
                if (editingDivision) await divisionService.updateDivision(editingDivision.id, data);
                else await divisionService.createDivision(data);
                setShowForm(false);
                await loadDivisions();
              }}
              onCancel={() => setShowForm(false)}
            />
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div
            className="mb-[14px] flex items-center gap-2 rounded-[4px] px-3 py-2.5 text-[12.5px]"
            style={{ background: 'var(--b-red-bg)', border: '1px solid var(--line)', color: 'var(--b-red-fg)' }}
          >
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {/* Table */}
        <div
          className="overflow-hidden rounded-[4px]"
          style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)' }}
        >
          <DataTable
            columns={columns}
            data={filteredDivisions}
            progressPending={loading}
            progressComponent={<LoadingComponent />}
            noDataComponent={<NoDataComponent />}
            customStyles={customStyles}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            highlightOnHover
            pointerOnHover
            responsive
            persistTableHead
          />
        </div>
      </div>
    </div>
  );
};

export default DivisionList;
